/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';
import moment from 'moment';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Link, Typography2 } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import { getLink, isOnDemand } from 'bundles/graphql/utils/CoursesV1';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import imgixParams from 'bundles/page/common/imgixParams';

import _t from 'i18n!nls/phoenix-cdp';

const styles = {
  courseCard: css`
    display: inline-block;
    min-height: 320px;
    padding: 5px 15px;
    vertical-align: top;
    width: 25%;

    img {
      height: auto;
      max-width: 100% !important; /* needed to override Imgix's style definition */
      width: 175px;

      &:hover {
        opacity: 0.8;
      }
    }

    @media (max-width: 480px) {
      text-align: center;
      width: 50%;
    }
  `,
  courseName: css`
    margin: 8px 0 3px 0;
    color: #0062e4;
    max-width: 100% !important; /* needed to override Imgix's style definition */
    width: 175px;

    @media (max-width: 480px) {
      margin: 8px auto;
    }
  `,
};

type Props = {
  course: CoursesV1;
  partnerShortName: string;
};

const Course = ({ course, partnerShortName }: Props) => {
  const formatDateMessage = (upcomingSessionStartDate: number) => {
    const now = moment();
    const momentStartDate = moment(upcomingSessionStartDate);
    const startDate = formatDateTimeDisplay(upcomingSessionStartDate, 'MMM DD, YYYY');

    const startDateMessage =
      now > momentStartDate ? (
        <FormattedMessage message={_t('Started {startDate}')} startDate={startDate} />
      ) : (
        <FormattedMessage message={_t('Starts {startDate}')} startDate={startDate} />
      );

    return startDateMessage;
  };

  const getFormattedStartDateForStanford = () => {
    let startDateMessage;
    if (course.courseStatus === 'preenroll') {
      const startDate = course.plannedLaunchDate;
      startDateMessage = <FormattedMessage message={_t('Starting {startDate}')} startDate={startDate} />;
    } else if (course.courseStatus === 'launched') {
      // Since Stanford isn't using sessions v2, we need to first check for the upcoming session start date to show "Started/Starts ..."
      if (course.upcomingSessionStartDate) {
        startDateMessage = formatDateMessage(course.upcomingSessionStartDate);
      } else if (isOnDemand(course)) {
        startDateMessage = _t('Available now');
      }
    }

    return startDateMessage;
  };

  const getFormattedStartDate = () => {
    let startDateMessage;

    if (partnerShortName === 'stanford') {
      startDateMessage = getFormattedStartDateForStanford();
    } else if (course.courseStatus === 'preenroll') {
      const startDate = course.plannedLaunchDate;
      startDateMessage = <FormattedMessage message={_t('Starting {startDate}')} startDate={startDate} />;
    } else if (course.courseStatus === 'launched') {
      if (isOnDemand(course)) {
        startDateMessage = _t('Available now');
      } else if (course.upcomingSessionStartDate) {
        startDateMessage = formatDateMessage(course.upcomingSessionStartDate);
      }
    }

    return startDateMessage;
  };

  return (
    <div css={styles.courseCard} className="rc-Course">
      <Link variant="standard" href={getLink(course)} data-e2e="partner-course-link">
        {course.photoUrl && (
          <Imgix
            src={course.photoUrl}
            alt={course.name}
            maxWidth={175}
            maxHeight={175}
            imgParams={imgixParams.fillWithWhiteBackground}
            className="card-one-clicker"
          />
        )}
        <Typography2 component="p" css={styles.courseName} variant="bodyPrimary">
          {course.name}
        </Typography2>
      </Link>
      <Typography2 component="p" variant="bodySecondary" color="supportText" className="plannedLaunchDate">
        {getFormattedStartDate()}
      </Typography2>
    </div>
  );
};

const courseFragment = gql`
  fragment PartnersCourseFragment on CoursesV1 {
    photoUrl
    name
    courseType
    startDate
    courseStatus
    upcomingSessionStartDate
    plannedLaunchDate
    s12nIds
    ...CoursesV1_getLink
    ...CoursesV1_isOnDemand
  }
  ${getLink.fragment}
  ${isOnDemand.fragment}
`;

export default withFragments({
  courseFragment,
})(Course);
